<div class="bk-page uf">
  <header class="bk-page__head uf__head">
    <div class="bk-page__intro">
      @if (isEdit) {
        <button type="button" class="bk-more uf__back" (click)="cancel()">
          <span class="bk-sym" aria-hidden="true">arrow_back</span>
          Back to application
        </button>
      } @else {
        <button type="button" class="bk-more uf__back" (click)="backToList()">
          <span class="bk-sym" aria-hidden="true">arrow_back</span>
          Applications
        </button>
      }
      <p class="bk-eyebrow">USSD application</p>
      <h1 class="bk-page__title">{{ title }}</h1>
      <p class="bk-page__sub">{{ subtitle }}</p>
    </div>
  </header>

  @if (isLoading) {
    <div class="uf__loading" role="status" aria-busy="true">
      <span class="bk-sr-only">Loading application</span>
      <span class="bk-skel" style="height: 44px" aria-hidden="true"></span>
      <span class="bk-skel uf__loading-panel" aria-hidden="true"></span>
    </div>
  } @else if (loadError) {
    <div class="bk-panel uf__state" role="alert">
      <span class="bk-sym uf__state-icon" aria-hidden="true">cloud_off</span>
      <p class="uf__state-title">{{ loadError }}</p>
      <div class="uf__state-actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="backToList()">Back to applications</button>
        <button type="button" class="bk-btn bk-btn--primary" (click)="loadApplication()">Try again</button>
      </div>
    </div>
  } @else {
    <alternative-stepper class="uf__steps" [steps]="steps" [noContent]="true" />

    <div class="uf__layout">
      @switch (activeStep) {
        <!-- ═══════════ STEP 2: CALLBACK ═══════════ -->
        @case ('callback') {
          <section class="bk-panel uf__panel" aria-labelledby="uf-callback-title">
            <h2 class="bk-eyebrow uf__panel-title uf__step-focus" id="uf-callback-title" tabindex="-1">Callback</h2>
            <p class="uf__panel-sub">Each step of a session is sent to this URL. Its reply is what the subscriber sees.</p>

            <div class="uf__fields">
              <div class="bk-field uf__full" [class.is-invalid]="!!callbackUrlError">
                <label class="bk-field__label" for="uf-url">Callback URL</label>
                <input
                  id="uf-url"
                  class="bk-field__input"
                  type="url"
                  inputmode="url"
                  autocomplete="off"
                  spellcheck="false"
                  placeholder="https://api.example.com/ussd"
                  [attr.aria-invalid]="!!callbackUrlError"
                  [attr.aria-describedby]="'uf-url-msg'"
                  [(ngModel)]="callbackUrl"
                  (ngModelChange)="callbackUrlError = ''"
                />
                @if (callbackUrlError) {
                  <p class="bk-field__error" id="uf-url-msg">
                    <span class="bk-sym" aria-hidden="true">error</span>{{ callbackUrlError }}
                  </p>
                } @else if (callbackIsInsecure) {
                  <p class="bk-field__hint uf__warn" id="uf-url-msg">
                    <span class="uf__dot" aria-hidden="true"></span>
                    This URL is not encrypted. Use https:// before you promote to production.
                  </p>
                } @else {
                  <p class="bk-field__hint" id="uf-url-msg">Use https://. Your service should reply within a few seconds.</p>
                }
              </div>

              <dl class="uf__fixed uf__full">
                <div>
                  <dt>Method</dt>
                  <dd>{{ method }}</dd>
                </div>
                <div>
                  <dt>Content type</dt>
                  <dd>{{ contentType }}</dd>
                </div>
              </dl>

              <div class="bk-field uf__full">
                <label class="bk-field__label" for="uf-auth">Authentication</label>
                <select
                  id="uf-auth"
                  class="bk-field__input"
                  aria-describedby="uf-auth-hint"
                  [ngModel]="authType"
                  (ngModelChange)="setAuthType($event)"
                >
                  @for (at of AUTH_TYPES; track at.value) {
                    <option [value]="at.value">{{ at.label }}</option>
                  }
                </select>
                <p class="bk-field__hint" id="uf-auth-hint">{{ authHint }}</p>
                @if (legacyAuthType) {
                  <p class="uf__note" role="note">
                    <span class="uf__dot uf__dot--bad" aria-hidden="true"></span>
                    This application was saved with {{ legacyAuthType }}, which the gateway does not support, so its
                    callbacks went out without credentials. Pick a supported method.
                  </p>
                }
              </div>

              @switch (authType) {
                @case ('BEARER') {
                  <div class="bk-field uf__full" [class.is-invalid]="!!credentialErrors['bearerToken']">
                    <label class="bk-field__label" for="uf-bearer">Token</label>
                    <div class="bk-field__wrap">
                      <input
                        id="uf-bearer"
                        [attr.aria-invalid]="!!credentialErrors['bearerToken']"
                        [attr.aria-describedby]="credentialErrors['bearerToken'] ? 'uf-bearer-msg' : null"
                        class="bk-field__input"
                        [type]="revealSecret ? 'text' : 'password'"
                        autocomplete="off"
                        spellcheck="false"
                        [(ngModel)]="bearerToken"
                        (ngModelChange)="clearCredentialError('bearerToken')"
                      />
                      <button
                        type="button"
                        class="bk-field__reveal"
                        [attr.aria-label]="revealSecret ? 'Hide token' : 'Show token'"
                        [attr.aria-pressed]="revealSecret"
                        (click)="revealSecret = !revealSecret"
                      >
                        <span class="bk-sym" aria-hidden="true">{{ revealSecret ? 'visibility_off' : 'visibility' }}</span>
                      </button>
                    </div>
                    @if (credentialErrors['bearerToken']) {
                      <p class="bk-field__error" id="uf-bearer-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['bearerToken'] }}</p>
                    }
                  </div>
                }
                @case ('BASIC') {
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['basicUsername']">
                    <label class="bk-field__label" for="uf-user">Username</label>
                    <input
                      id="uf-user"
                      [attr.aria-invalid]="!!credentialErrors['basicUsername']"
                      [attr.aria-describedby]="credentialErrors['basicUsername'] ? 'uf-user-msg' : null"
                      class="bk-field__input"
                      type="text"
                      autocomplete="off"
                      [(ngModel)]="basicUsername"
                      (ngModelChange)="clearCredentialError('basicUsername')"
                    />
                    @if (credentialErrors['basicUsername']) {
                      <p class="bk-field__error" id="uf-user-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['basicUsername'] }}</p>
                    }
                  </div>
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['basicPassword']">
                    <label class="bk-field__label" for="uf-pass">Password</label>
                    <div class="bk-field__wrap">
                      <input
                        id="uf-pass"
                        [attr.aria-invalid]="!!credentialErrors['basicPassword']"
                        [attr.aria-describedby]="credentialErrors['basicPassword'] ? 'uf-pass-msg' : null"
                        class="bk-field__input"
                        [type]="revealSecret ? 'text' : 'password'"
                        autocomplete="new-password"
                        [(ngModel)]="basicPassword"
                        (ngModelChange)="clearCredentialError('basicPassword')"
                      />
                      <button
                        type="button"
                        class="bk-field__reveal"
                        [attr.aria-label]="revealSecret ? 'Hide password' : 'Show password'"
                        [attr.aria-pressed]="revealSecret"
                        (click)="revealSecret = !revealSecret"
                      >
                        <span class="bk-sym" aria-hidden="true">{{ revealSecret ? 'visibility_off' : 'visibility' }}</span>
                      </button>
                    </div>
                    @if (credentialErrors['basicPassword']) {
                      <p class="bk-field__error" id="uf-pass-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['basicPassword'] }}</p>
                    }
                  </div>
                }
                @case ('API_KEY') {
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['apiKeyHeader']">
                    <label class="bk-field__label" for="uf-key-header">Header name</label>
                    <input
                      id="uf-key-header"
                      [attr.aria-invalid]="!!credentialErrors['apiKeyHeader']"
                      [attr.aria-describedby]="credentialErrors['apiKeyHeader'] ? 'uf-key-header-msg' : null"
                      class="bk-field__input"
                      type="text"
                      autocomplete="off"
                      spellcheck="false"
                      placeholder="X-API-Key"
                      [(ngModel)]="apiKeyHeader"
                      (ngModelChange)="clearCredentialError('apiKeyHeader')"
                    />
                    @if (credentialErrors['apiKeyHeader']) {
                      <p class="bk-field__error" id="uf-key-header-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['apiKeyHeader'] }}</p>
                    }
                  </div>
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['apiKey']">
                    <label class="bk-field__label" for="uf-key">Key</label>
                    <div class="bk-field__wrap">
                      <input
                        id="uf-key"
                        [attr.aria-invalid]="!!credentialErrors['apiKey']"
                        [attr.aria-describedby]="credentialErrors['apiKey'] ? 'uf-key-msg' : null"
                        class="bk-field__input"
                        [type]="revealSecret ? 'text' : 'password'"
                        autocomplete="off"
                        spellcheck="false"
                        [(ngModel)]="apiKey"
                        (ngModelChange)="clearCredentialError('apiKey')"
                      />
                      <button
                        type="button"
                        class="bk-field__reveal"
                        [attr.aria-label]="revealSecret ? 'Hide key' : 'Show key'"
                        [attr.aria-pressed]="revealSecret"
                        (click)="revealSecret = !revealSecret"
                      >
                        <span class="bk-sym" aria-hidden="true">{{ revealSecret ? 'visibility_off' : 'visibility' }}</span>
                      </button>
                    </div>
                    @if (credentialErrors['apiKey']) {
                      <p class="bk-field__error" id="uf-key-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['apiKey'] }}</p>
                    }
                  </div>
                }
                @case ('CUSTOM') {
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['customAuthHeader']">
                    <label class="bk-field__label" for="uf-custom-header">Header name</label>
                    <input
                      id="uf-custom-header"
                      [attr.aria-invalid]="!!credentialErrors['customAuthHeader']"
                      [attr.aria-describedby]="credentialErrors['customAuthHeader'] ? 'uf-custom-header-msg' : null"
                      class="bk-field__input"
                      type="text"
                      autocomplete="off"
                      spellcheck="false"
                      placeholder="X-Signature"
                      [(ngModel)]="customAuthHeader"
                      (ngModelChange)="clearCredentialError('customAuthHeader')"
                    />
                    @if (credentialErrors['customAuthHeader']) {
                      <p class="bk-field__error" id="uf-custom-header-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['customAuthHeader'] }}</p>
                    }
                  </div>
                  <div class="bk-field" [class.is-invalid]="!!credentialErrors['customAuthValue']">
                    <label class="bk-field__label" for="uf-custom-value">Header value</label>
                    <div class="bk-field__wrap">
                      <input
                        id="uf-custom-value"
                        [attr.aria-invalid]="!!credentialErrors['customAuthValue']"
                        [attr.aria-describedby]="credentialErrors['customAuthValue'] ? 'uf-custom-value-msg' : null"
                        class="bk-field__input"
                        [type]="revealSecret ? 'text' : 'password'"
                        autocomplete="off"
                        spellcheck="false"
                        [(ngModel)]="customAuthValue"
                        (ngModelChange)="clearCredentialError('customAuthValue')"
                      />
                      <button
                        type="button"
                        class="bk-field__reveal"
                        [attr.aria-label]="revealSecret ? 'Hide value' : 'Show value'"
                        [attr.aria-pressed]="revealSecret"
                        (click)="revealSecret = !revealSecret"
                      >
                        <span class="bk-sym" aria-hidden="true">{{ revealSecret ? 'visibility_off' : 'visibility' }}</span>
                      </button>
                    </div>
                    @if (credentialErrors['customAuthValue']) {
                      <p class="bk-field__error" id="uf-custom-value-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ credentialErrors['customAuthValue'] }}</p>
                    }
                  </div>
                }
              }
            </div>

            <div class="uf__actions">
              <button type="button" class="bk-btn bk-btn--ghost" (click)="prevStep()">
                <span class="bk-sym" aria-hidden="true">arrow_back</span>
                Back
              </button>
              <button type="button" class="bk-btn bk-btn--primary" (click)="nextStep()">
                Continue to review
                <span class="bk-sym uf__trail" aria-hidden="true">arrow_forward</span>
              </button>
            </div>
          </section>

          <aside class="uf__aside" aria-label="About callbacks">
            <p class="bk-eyebrow">How callbacks work</p>
            <ul class="uf__tips">
              <li>The gateway sends a {{ method }} with the session ID, the number and what the subscriber typed.</li>
              <li>Reply with the next screen. Keep it under 182 characters, or 80 if it uses characters outside GSM-7.</li>
              <li>Credentials are stored with the application and sent on every request.</li>
            </ul>
          </aside>
        }

        <!-- ═══════════ STEP 3: REVIEW ═══════════ -->
        @case ('review') {
          <section class="uf__review uf__step-focus" aria-label="Review" tabindex="-1">
            <div class="bk-panel uf__panel">
              <div class="uf__panel-head">
                <h2 class="bk-eyebrow uf__panel-title">Basics</h2>
                <button type="button" class="bk-more" aria-label="Change basics" (click)="goToStep('basics')">
                  Change
                  <span class="bk-sym" aria-hidden="true">arrow_outward</span>
                </button>
              </div>
              <dl class="bk-kv">
                <dt>Name</dt>
                <dd>{{ name || '—' }}</dd>
                <dt>Alias</dt>
                <dd>{{ alias || '—' }}</dd>
                <dt>Country</dt>
                <dd>{{ selectedCountry ? selectedCountry.name + ' (' + selectedCountry.code + ')' : '—' }}</dd>
                <dt>Service code</dt>
                <dd><span class="uf__code">{{ serviceCode || '—' }}</span></dd>
                @if (serviceName) {
                  <dt>Service name</dt>
                  <dd>{{ serviceName }}</dd>
                }
                @if (description) {
                  <dt>Description</dt>
                  <dd>{{ description }}</dd>
                }
              </dl>
            </div>

            <div class="bk-panel uf__panel">
              <div class="uf__panel-head">
                <h2 class="bk-eyebrow uf__panel-title">Callback</h2>
                <button type="button" class="bk-more" aria-label="Change callback" (click)="goToStep('callback')">
                  Change
                  <span class="bk-sym" aria-hidden="true">arrow_outward</span>
                </button>
              </div>
              <dl class="bk-kv">
                <dt>URL</dt>
                <dd class="uf__url">{{ callbackUrl || '—' }}</dd>
                <dt>Method</dt>
                <dd>{{ method }}, {{ contentType }}</dd>
                <dt>Authentication</dt>
                <dd>{{ authLabel }}</dd>
                @switch (authType) {
                  @case ('BEARER') {
                    <dt>Token</dt>
                    <dd>{{ bearerToken ? 'Set' : 'Not set' }}</dd>
                  }
                  @case ('BASIC') {
                    <dt>Username</dt>
                    <dd>{{ basicUsername || '—' }}</dd>
                    <dt>Password</dt>
                    <dd>{{ basicPassword ? 'Set' : 'Not set' }}</dd>
                  }
                  @case ('API_KEY') {
                    <dt>Header</dt>
                    <dd>{{ apiKeyHeader || '—' }}</dd>
                    <dt>Key</dt>
                    <dd>{{ apiKey ? 'Set' : 'Not set' }}</dd>
                  }
                  @case ('CUSTOM') {
                    <dt>Header</dt>
                    <dd>{{ customAuthHeader || '—' }}</dd>
                    <dt>Value</dt>
                    <dd>{{ customAuthValue ? 'Set' : 'Not set' }}</dd>
                  }
                }
              </dl>
            </div>

            @if (!isEdit) {
              <p class="uf__line">
                <span class="bk-sym" aria-hidden="true">info</span>
                The application starts in Development. Promote it to Sandbox, UAT and Production from its page.
              </p>
            }

            @if (submitError) {
              <div class="bk-panel uf__error" role="alert">
                <span class="uf__dot uf__dot--bad" aria-hidden="true"></span>
                <p>{{ submitError }}</p>
              </div>
            }

            <div class="uf__actions">
              <button type="button" class="bk-btn bk-btn--ghost" [disabled]="isSubmitting" (click)="prevStep()">
                <span class="bk-sym" aria-hidden="true">arrow_back</span>
                Back
              </button>
              <button
                type="button"
                class="bk-btn bk-btn--primary"
                [disabled]="isSubmitting"
                [attr.aria-busy]="isSubmitting || null"
                (click)="submit()"
              >
                {{ submitError && !isSubmitting ? (isEdit ? 'Try saving again' : 'Try creating again') : submitLabel }}
              </button>
            </div>
          </section>
        }

        <!-- ═══════════ STEP 1: BASICS ═══════════ -->
        @default {
          <section class="bk-panel uf__panel" aria-labelledby="uf-basics-title">
            <h2 class="bk-eyebrow uf__panel-title uf__step-focus" id="uf-basics-title" tabindex="-1">Basics</h2>
            <p class="uf__panel-sub">What the application is called and the code subscribers dial.</p>

            <div class="uf__fields">
              <div class="bk-field uf__full" [class.is-invalid]="!!nameError || nameTaken">
                <label class="bk-field__label" for="uf-name">Name</label>
                <input
                  id="uf-name"
                  class="bk-field__input"
                  type="text"
                  autocomplete="off"
                  placeholder="e.g. Balance check"
                  aria-describedby="uf-name-msg"
                  [attr.aria-invalid]="!!nameError || nameTaken"
                  [(ngModel)]="name"
                  (ngModelChange)="onNameChange()"
                />
                @if (nameError) {
                  <p class="bk-field__error" id="uf-name-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ nameError }}</p>
                } @else if (nameTaken) {
                  <p class="bk-field__error" id="uf-name-msg"><span class="bk-sym" aria-hidden="true">error</span>Another application already uses this name.</p>
                } @else {
                  <p class="bk-field__hint" id="uf-name-msg">Shown in the console and in reports, not to subscribers.</p>
                }
              </div>

              <div class="bk-field">
                <label class="bk-field__label" for="uf-alias">Alias</label>
                <input
                  id="uf-alias"
                  class="bk-field__input"
                  type="text"
                  autocomplete="off"
                  spellcheck="false"
                  placeholder="BALANCE_CHECK"
                  [(ngModel)]="alias"
                  (ngModelChange)="onAliasChange()"
                />
                <p class="bk-field__hint">
                  {{ aliasTouched ? 'Capitals, digits and underscores.' : 'Follows the name until you change it.' }}
                </p>
              </div>

              <div class="bk-field">
                <label class="bk-field__label" for="uf-service-name">Service name (optional)</label>
                <input
                  id="uf-service-name"
                  class="bk-field__input"
                  type="text"
                  autocomplete="off"
                  spellcheck="false"
                  placeholder="balance_check_svc"
                  [(ngModel)]="serviceName"
                />
                <p class="bk-field__hint">Sent to your callback so one URL can serve several apps.</p>
              </div>

              <!-- Country and code: one registration -->
              <div class="uf__full uf__reg">
                <div class="bk-field uf__country" [class.is-invalid]="!!countryError">
                  @if (selectedCountry && !showCountryList) {
                    <!-- The search input is gone once a country is picked, so the label names the group -->
                    <span class="bk-field__label" id="uf-country-label">Country</span>
                    <div
                      class="bk-field__control uf__picked"
                      role="group"
                      aria-labelledby="uf-country-label"
                      [class.is-locked]="codeLocked"
                    >
                      <span class="uf__picked-name">{{ selectedCountry.name }}</span>
                      <span class="uf__picked-code">{{ selectedCountry.code }}</span>
                      @if (!codeLocked) {
                        <button type="button" class="bk-icon-btn uf__picked-clear" aria-label="Change country" (click)="clearCountry()">
                          <span class="bk-sym" aria-hidden="true">close</span>
                        </button>
                      }
                    </div>
                  } @else {
                    <label class="bk-field__label" for="uf-country">Country</label>
                    <div class="uf__combo">
                      <input
                        #countryInput
                        id="uf-country"
                        class="bk-field__input"
                        type="text"
                        role="combobox"
                        autocomplete="off"
                        placeholder="Search countries"
                        aria-autocomplete="list"
                        [attr.aria-expanded]="showCountryList"
                        [attr.aria-controls]="countryListId"
                        [attr.aria-activedescendant]="activeOptionId()"
                        [attr.aria-invalid]="!!countryError"
                        [attr.aria-describedby]="countryError ? 'uf-country-msg' : null"
                        [(ngModel)]="countrySearch"
                        (ngModelChange)="onCountrySearch()"
                        (focus)="openCountryList()"
                        (click)="openCountryList()"
                        (blur)="onCountryBlur($event)"
                        (keydown)="onCountryKeydown($event)"
                      />
                      @if (showCountryList) {
                        <ul class="uf__list" role="listbox" [id]="countryListId" aria-label="Countries">
                          @for (country of filteredCountries; track country.code; let i = $index) {
                            <li
                              role="option"
                              class="uf__option"
                              [id]="countryListId + '-' + i"
                              [class.is-active]="i === activeCountryIndex"
                              [attr.aria-selected]="selectedCountry?.code === country.code"
                              (mousedown)="$event.preventDefault()"
                              (click)="selectCountry(country)"
                              (mouseenter)="activeCountryIndex = i"
                            >
                              <span>{{ country.name }}</span>
                              <span class="uf__option-code">{{ country.code }} {{ country.dialCode }}</span>
                            </li>
                          } @empty {
                            <li class="uf__option uf__option--empty" role="option" aria-disabled="true">
                              No country matches “{{ countrySearch }}”
                            </li>
                          }
                        </ul>
                      }
                    </div>
                  }
                  @if (countryError) {
                    <p class="bk-field__error" id="uf-country-msg"><span class="bk-sym" aria-hidden="true">error</span>{{ countryError }}</p>
                  }
                </div>

                <div class="bk-field uf__codefield" [class.is-invalid]="!!serviceCodeError || codeTaken || codeState === 'error'">
                  <span class="bk-field__label" id="uf-code-label">Service code</span>
                  <div class="uf__codebox" role="status" aria-labelledby="uf-code-label">
                    @if (codeState === 'loading') {
                      <span class="bk-skel uf__code-skel" aria-hidden="true"></span>
                      <span class="bk-sr-only">Getting a code</span>
                    } @else if (serviceCode) {
                      <span class="uf__code uf__code--big">{{ serviceCode }}</span>
                    } @else {
                      <span class="uf__code-empty">{{ selectedCountry ? 'No code yet' : 'Pick a country first' }}</span>
                    }
                  </div>
                  @if (codeState === 'error') {
                    <p class="bk-field__error"><span class="bk-sym" aria-hidden="true">error</span>{{ codeError }}</p>
                  } @else if (serviceCodeError) {
                    <p class="bk-field__error"><span class="bk-sym" aria-hidden="true">error</span>{{ serviceCodeError }}</p>
                  } @else if (codeTaken) {
                    <p class="bk-field__error"><span class="bk-sym" aria-hidden="true">error</span>Another application already uses this code.</p>
                  } @else if (codeLocked) {
                    <p class="bk-field__hint">Subscribers already dial this code, so it stays unless you change it on purpose.</p>
                  } @else {
                    <p class="bk-field__hint">A test code, generated for the country you pick.</p>
                  }
                </div>

                <div class="uf__reg-actions">
                  @if (codeState === 'error' || codeTaken || (selectedCountry && !serviceCode && codeState === 'idle' && !codeLocked)) {
                    <button type="button" class="bk-btn bk-btn--ghost" (click)="fetchServiceCode()">
                      <span class="bk-sym" aria-hidden="true">refresh</span>
                      {{ codeTaken ? 'Get another code' : 'Try again' }}
                    </button>
                  }
                  @if (isEdit && codeLocked) {
                    <button type="button" class="bk-btn bk-btn--ghost" (click)="requestCodeChange()">
                      <span class="bk-sym" aria-hidden="true">swap_horiz</span>
                      Change country and code
                    </button>
                  }
                  @if (isEdit && codeUnlocked) {
                    <button type="button" class="bk-btn bk-btn--ghost" (click)="keepOriginalCode()">
                      Keep {{ original?.serviceCode || 'the current code' }}
                    </button>
                  }
                </div>
              </div>

              <div class="bk-field uf__full">
                <label class="bk-field__label" for="uf-desc">Description (optional)</label>
                <textarea
                  id="uf-desc"
                  class="bk-field__input"
                  rows="3"
                  placeholder="What subscribers can do with this menu"
                  [(ngModel)]="description"
                ></textarea>
              </div>
            </div>

            <div class="uf__actions">
              <button type="button" class="bk-btn bk-btn--ghost" (click)="cancel()">Cancel</button>
              <button type="button" class="bk-btn bk-btn--primary" [disabled]="codeState === 'loading'" (click)="nextStep()">
                Continue to callback
                <span class="bk-sym uf__trail" aria-hidden="true">arrow_forward</span>
              </button>
            </div>
          </section>

          <aside class="uf__aside" aria-label="About service codes">
            <p class="bk-eyebrow">Before you start</p>
            <ul class="uf__tips">
              <li>Each application gets its own service code, such as *384*12345#, in the country you pick.</li>
              <li>New applications run in Development. Test them in the simulator, then promote them one stage at a time.</li>
              <li>Production needs a reviewer's approval.</li>
            </ul>
          </aside>
        }
      }
    </div>
  }
</div>

<app-prompt-card
  title="Change the service code?"
  buttonText="Change code"
  closeText="Keep the code"
  tone="default"
  impact="Subscribers who dial the current code stop reaching this application once you save."
  impactIcon="warning"
  [openModal]="showChangeCodeConfirm"
  (onClose)="cancelCodeChange()"
  (onButtonClick)="confirmCodeChange()"
>
  <p class="uf__confirm">
    You can pick a country and get a new code. <strong>{{ original?.serviceCode }}</strong> stays until you save.
  </p>
</app-prompt-card>
